import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { 
  FormControl, 
  Validators, 
  FormsModule, 
  ReactiveFormsModule, 
  AbstractControl, 
  ValidationErrors, 
  ValidatorFn,
  FormGroup,
  FormBuilder,
  FormArray
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { 
  Observable, 
  debounceTime, 
  distinctUntilChanged, 
  filter, 
  finalize, 
  forkJoin,
  map, 
  of, 
  startWith, 
  switchMap
} from 'rxjs';
import { Store } from '@ngrx/store';
import { UserBiqData } from '@app/schemas/biq';
import { GooglePlacesService } from '@app/services/google/places/google-places.service';
import { AsyncPipe } from '@angular/common';
import { 
  BIQ_ETHNICITY, 
  BIQ_GENDER, 
  JOB_OPTIONS, 
  MY_PROFILE_LINKS, 
  SCHOOL_NAMES, 
  SCHOOL_YEAR 
} from '@app/utils/constants';
import { COURSE_MAJOR_CIP, MajorType } from '@app/utils/CIP-code';
import { generateGraduationYear, generateYears } from '@app/utils/sharedFunctions';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { ConfigService } from '@app/services/config-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinnerModule } from "ngx-spinner";
import { LoaderOverlayComponent } from '@app/shared/loader-overlay/loader-overlay.component';
import { SurveysService } from '@app/services/api/surveys/surveys.service';
import { environment } from '@environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ScoresComponent } from '@app/shared/survey/scores/scores.component';
import { AddResumeDialogComponent } from '@app/shared/results/add-resume-dialog/add-resume-dialog.component';
import { UserBiqService } from '@app/services/api/user-biq/user-biq.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { SuccessSnackBarComponent } from '@app/shared/snack-bars/success/success.component';
import { SuccessDefaultOptions } from '@app/shared/snack-bars/success/success.utils';
import { UserService, UserProfile } from '@app/services/api/user/user.service';
import { versionInfo } from '@app/version';
import { selfDescibeTextValidator } from '@app/validations/textValidations';
import { ProfileUpdateService } from '@app/services/signals/profile-update-service';
import { AuthService } from '@app/services/auth/auth.service';
import { TenantId } from '@app/enums/TenantId';
import { ReportedWorkExperienceService, ReportedWorkExperience } from '@app/services/api/reported-work-experience/reported-work-experience.service';
import { FORTUNE_1000_COMPANIES } from '@app/shared/dialogs/job-landing-dialog/fortune-1000-companies';
import { JobLandingDialogComponent, JobLandingDialogResult } from '@app/shared/dialogs/job-landing-dialog/job-landing-dialog.component';
import { WORK_TYPE_OPTIONS, formatWorkType } from '@app/utils/work-type-config';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { updateUserBiq } from '@app/state/actions/biq.action';

@Component({
  selector: 'app-my-profile',
  imports: [
    MatButtonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    AsyncPipe,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    NgxSpinnerModule,
    LoaderOverlayComponent,
    MatTooltipModule,
  ],
  templateUrl: './my-profile.component.html',
  styleUrl: './my-profile.component.css'
})
export class MyProfileComponent implements OnInit {
  @ViewChild('contentDiv') contentDiv!: ElementRef<HTMLDivElement>;
  public userForm: FormGroup;
  public emailPreferencesFormGroup: FormGroup;
  private emailPreferencesLoaded: boolean = false;
  public appVersion = versionInfo.version;
  public lastUpdated = versionInfo.lastUpdated;

  public filteredLocations!: Observable<string[]>;
  public availableLocations: string[] = [];
  public selectedLocation: string | null = null;

  public filteredWorkLocations!: Observable<any[]>;
  public availableWorkLocations: string[] = [];
  public selectedWorkLocation: string | null = null;
  public selectedWorkLocationPlaceId: string | null = null;
  public selectedAdministrativeAreaLevel2: string | null = null;

  public schoolYearOptions = SCHOOL_YEAR;
  public schoolGraduationOptions: { label: string, value: string }[] = [];
  public schoolNameOptions = SCHOOL_NAMES;

  public courseMajorOptions: MajorType[] = COURSE_MAJOR_CIP;
  public filteredMajorOptions!: Observable<MajorType[]>;
  public jobOptions = JOB_OPTIONS;

  public birthYears: number[] = [];
  public filteredBirthYears!: Observable<number[]>;

  public ethnicityOptions = BIQ_ETHNICITY;
  public ethnicityDisableOptions = ['Prefer not to answer', 'self-describe'];

  public genderOptions = BIQ_GENDER;

  public additionalLinks = MY_PROFILE_LINKS;
  public edgeCommunicationAssessmentUrl: string;
  public edgeBehavioralAssessmentUrl: string;

  public storedBiqData!: UserBiqData;
  private scrollTimeout: any;
  public isSaveBtnEnabled: boolean = false;

  public sideNavSections = [
    { id: 'section-name', label: 'Name' },
    { id: 'section-location', label: 'Location' },
    { id: 'section-education', label: 'Education & Employment' },
    { id: 'section-background', label: 'Background' },
    { id: 'section-email', label: 'Email Preferences' },
    { id: 'section-additional', label: 'Additional Information' },
  ];
  public activeSection = 'section-name';
  public isSaving: boolean = false;
  public spinnerLoadingMessage: string = '';
  private biqDataLoaded: boolean = false;
  readonly dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  public isAUTenant: boolean = false;
  private router = inject(Router);
  public riasecScore: any = null;
  public reportedWorkExperiences: ReportedWorkExperience[] = [];
  private reportedWorkExperienceService = inject(ReportedWorkExperienceService);
  private pendingRemovals: Set<number> = new Set();
  private pendingUpdates: Set<number> = new Set();
  private originalWorkExperiences: Map<number, ReportedWorkExperience> = new Map();

  public workTypeOptions = WORK_TYPE_OPTIONS;

  public orgFilteredMap: Map<number, Observable<string[]>> = new Map();
  public orgControlMap: Map<number, FormControl<string>> = new Map();

  constructor(
    private store: Store<{ userBiq: UserBiqData }>,
    private googlePlaces: GooglePlacesService,
    private fb: FormBuilder, 
    private cdr: ChangeDetectorRef,
    private configService: ConfigService,
    private spinner: NgxSpinnerService,
    private surveyService: SurveysService,
    private biqApiService: UserBiqService,
    private userService: UserService,
    private profileService: ProfileUpdateService,
    private authService: AuthService,
  ) {
    // Check if tenant is American University
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    this.isAUTenant = tenantId === TenantId.AU || tenantId === TenantId.AU_MOCK;

    this.birthYears = generateYears();

    this.emailPreferencesFormGroup = this.fb.group({
      productEmails: new FormControl(false),
      marketingEmails: new FormControl(false),
    });

    this.userForm = this.fb.group({
      email: new FormControl({ value: '', disabled: true }),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      location: new FormControl('', [
        Validators.required, 
        this.validateLocation.bind(this)
      ]),
      workLocation: new FormControl('', [
        Validators.required, 
        this.validateWorkLocation.bind(this)
      ]),
      schoolYear: new FormControl('', [Validators.required]),
      graduationYear: new FormControl('', [Validators.required]),
      schoolName: new FormControl('', [Validators.required]),
      graduationMajor: new FormControl<MajorType | null>(null, [
        this.majorMatchValidator(this.courseMajorOptions),
      ]),
      isWorking: new FormControl(false),
      jobType: new FormControl<null | 'part_time' | 'full_time'>(null),
      birthYear: new FormControl<number | null>(null, [
        Validators.required,
        this.validBirthYearValidator(this.birthYears)
      ]),
      birthYearNotToSay: new FormControl(false),
      ethnicities: this.fb.array([], this.minSelectedCheckboxes(1)),
      ethnicitySelfDescribe: new FormControl(false),
      ethnicitySelfDescribeText: new FormControl({ value: '', disabled: false }, [Validators.maxLength(250)]),
      gender: new FormControl('', [Validators.required]),
      genderSelfDescribeText: new FormControl({ value: '', disabled: false }, [Validators.maxLength(250)]),
    });

    this.addCheckboxes();

    // Auto-set and disable school name for AU tenants
    if (this.isAUTenant) {
      this.userForm.get('schoolName')?.setValue('American University');
      this.userForm.get('schoolName')?.disable();
    }

    this.edgeCommunicationAssessmentUrl = this.configService.edgeCommuunicationAssessmentUrl;
    this.edgeBehavioralAssessmentUrl = this.configService.edgeBehavioralAssessmentUrl;
  }

  ngOnInit() {
    this.userService.getMe().subscribe({
      next: (profile: UserProfile) => {
        this.userForm.get('email')?.setValue(profile.email);
      },
    });

    this.userService.getEmailPreferences().subscribe({
      next: (prefs) => {
        prefs.emailPreferences.forEach((pref: any) => {
          if (pref.emailType === 'product') {
            this.emailPreferencesFormGroup.get('productEmails')?.setValue(pref.subscribed);
          } else if (pref.emailType === 'marketing') {
            this.emailPreferencesFormGroup.get('marketingEmails')?.setValue(pref.subscribed);
          }
        });

        this.emailPreferencesLoaded = true;
      },
    });

    this.loadReportedWorkExperiences();

    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      this.selectedLocation = value.location;
      this.selectedWorkLocation = value.futureLocation;
      this.selectedWorkLocationPlaceId = value.futureLocationGooglePlaceId ?? null;
      this.selectedAdministrativeAreaLevel2 = value.futureLocationAdministrativeAreaLevel2 ?? null;

      this.userForm.patchValue({
        firstName: value.firstName,
        lastName: value.lastName,
        location: value.location,
        workLocation: value.futureLocation,
        schoolYear: value.grade,
        graduationYear: value.graduationYear,
        schoolName: value.schoolName,
        graduationMajor: this.courseMajorOptions.find(e => e.value == value.major) || null,
        isWorking: value.isWorking,
        jobType: value.jobType || null,
        birthYear: value.birthYear,
        birthYearNotToSay: value.birthYear ? false : true,
      });
      this.restoreEthnicitySelections(value.ethnicity);
      this.restoreGender(value.gender);
      this.biqDataLoaded = true;
    });

    this.filteredLocations = this.userForm.get('location')!.valueChanges.pipe(
      startWith(''),
      filter((value): value is string => typeof value === 'string'),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value.length > 2) {
          return this.googlePlaces.getPlacePredictions(value).pipe(
            map((results) => {
              this.availableLocations = results;
              return results;
            })
          );
        } else {
          this.availableLocations = [];
          return of([]);
        }
      })
    );

    this.filteredWorkLocations = this.userForm.get('workLocation')!.valueChanges.pipe(
      startWith(''),
      filter((value): value is string => typeof value === 'string'),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value.length > 2) {
          return this.googlePlaces.getPlacePredictionsWithDetails(value).pipe(
            map((results) => {
              this.availableWorkLocations = results.map(r => r.description);
              return results;
            })
          );
        } else {
          this.availableWorkLocations = [];
          return of([]);
        }
      })
    );

    this.userForm.get('workLocation')?.updateValueAndValidity();
    this.userForm.get('location')?.updateValueAndValidity();

    this.schoolGraduationOptions = generateGraduationYear(2025);
    this.schoolGraduationOptions.push({ label: 'Other', value: 'Other' });

    this.filteredMajorOptions = this.userForm.get('graduationMajor')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value : value?.label ?? '';
        return this._filterMajorOption(filterValue);
      })
    );

    this.filteredBirthYears = this.userForm.get('birthYear')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterBirthYears(value || ''))
    );
    this.userForm.get('birthYearNotToSay')!.valueChanges.subscribe(
    (preferNotToSay: boolean | null) => {
      if (preferNotToSay) {
        this.userForm.get('birthYear')?.reset();
        this.userForm.get('birthYear')?.disable();
      } else {
        this.userForm.get('birthYear')?.enable();
      }
    });

    // Initialize "prefer not to say" state manually based on loaded BIQ data
    if (this.userForm.get('birthYearNotToSay')?.value) {
      this.userForm.get('birthYear')?.reset();
      this.userForm.get('birthYear')?.disable();
    }

    this.userForm.get('ethnicities')?.valueChanges.subscribe(() => {
      this.evaluateSelections();
    });
    
    // Re-evaluate form when user checks/unchecks self describe custom option
    this.userForm.get('ethnicitySelfDescribe')?.valueChanges.subscribe(() => {
      this.evaluateSelections();
    });

    this.userForm.get('gender')?.valueChanges.subscribe(value => {
      this.userForm.get('genderSelfDescribeText')?.setValue('');
      if (value === 'self-describe') {
        this.userForm.get('genderSelfDescribeText')?.setValidators([selfDescibeTextValidator()]);
        this.userForm.get('genderSelfDescribeText')?.enable();
      } else {
        this.userForm.get('genderSelfDescribeText')?.clearValidators();
        this.userForm.get('genderSelfDescribeText')?.setValue('');
        this.userForm.get('genderSelfDescribeText')?.disable();
      }

      this.userForm.get('genderSelfDescribeText')?.updateValueAndValidity();
    });

    [this.userForm, this.emailPreferencesFormGroup].forEach(formGroup => {
      formGroup.valueChanges.subscribe(() => {
        this.formIsLoaded() && this.checkIfFormChanged();
      });
    });

    /**
     * Check if user has taken career survey and has a RIASEC score, 
     * if not, do not show Career Survey and the Edge result links on My Profile page
     */
    this.getSurveyResults();
  }

  formIsLoaded(): boolean {
    return this.biqDataLoaded && this.emailPreferencesLoaded;
  }

  checkIfFormChanged() {
    this.isSaveBtnEnabled = this.userForm.dirty || this.emailPreferencesFormGroup.dirty || this.hasWorkExperienceChanges();
  }

  hasWorkExperienceChanges(): boolean {
    return this.pendingRemovals.size > 0 || this.pendingUpdates.size > 0;
  }

  private majorMatchValidator(validOptions: MajorType[]) {
    return (control: AbstractControl): ValidationErrors | null => {
      const inputValue = control.value;
      if (!inputValue) return null;
  
      const match = validOptions.find(e => e.value == inputValue.value);
      return match ? null : { invalidMajor: true };
    };
  }

  private _filterMajorOption(value: string): MajorType[] {
    const filterVal = value.toLowerCase();
    return this.courseMajorOptions.filter(
      loc => loc.label.toLowerCase().includes(filterVal)
    );
  }

  private validBirthYearValidator(validYears: number[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const inputValue = control.value;
      if (inputValue === null || inputValue === undefined || inputValue === '') {
        return null;
      }

      const numericValue = Number(inputValue);
      return validYears.includes(numericValue) ? null : { invalidYear: true };
    };
  }

  private _filterBirthYears(value: string | number): number[] {
    const filterValue = value.toString();
    return this.birthYears.filter(year => year.toString().startsWith(filterValue));
  }

  private addCheckboxes() {
    this.ethnicityOptions.forEach(() => {
      (this.userForm.get('ethnicities') as FormArray).push(new FormControl(false));
    });
  }

  private restoreEthnicitySelections(ethnicities: string[]) {
    const formArray = this.userForm.get('ethnicities') as FormArray;
    
    // Reset all checkboxes first
    formArray.controls.forEach(control => control.setValue(false, { emitEvent: false }));
    this.userForm.get('ethnicitySelfDescribe')?.setValue(false, { emitEvent: false });
    this.userForm.get('ethnicitySelfDescribeText')?.setValue('', { emitEvent: false });
    
    ethnicities.forEach(ethnicity => {
      const optionIndex = this.ethnicityOptions.findIndex(option => option.value === ethnicity);
      if (optionIndex >= 0) {
        formArray.at(optionIndex).setValue(true, { emitEvent: false });
      } else {
        this.userForm.get('ethnicitySelfDescribe')?.setValue(true, { emitEvent: false });
        this.userForm.get('ethnicitySelfDescribeText')?.setValue(ethnicity, { emitEvent: false });
      }
    });
    this.userForm.get('ethnicities')?.updateValueAndValidity();
  }

  private restoreGender(value: string) {
    const genderIndex = this.genderOptions.findIndex(e => e.value == value);
    if (genderIndex >= 0) {
      this.userForm.get('gender')?.setValue(value);
    } else {
      this.userForm.get('gender')?.setValue('self-describe');
      this.userForm.get('genderSelfDescribeText')?.setValue(value);
    }
  }

  displayMajorLabel = (major: MajorType): string => {
    return major?.label ?? '';
  };

  onLocationSelected(event: any) {
    const value = event.option.value;
    this.selectedLocation = value;
    this.userForm.get('location')?.setErrors(null);
    this.userForm.get('location')?.updateValueAndValidity();
  }

  onWorkLocationSelected(event: any) {
    const value = event.option.value;
    this.selectedWorkLocation = value.description || value;
    this.selectedWorkLocationPlaceId = value.placeId || null;
    
    // Update form control with the string description, not the object
    this.userForm.get('workLocation')?.setValue(this.selectedWorkLocation, { emitEvent: false });
    
    // Fetch and store administrative_area_level_2 when location is selected
    if (this.selectedWorkLocationPlaceId) {
      this.googlePlaces.getPlaceDetails(this.selectedWorkLocationPlaceId).subscribe(place => {
        const county = this.googlePlaces.extractAdministrativeAreaLevel2(place);
        this.selectedAdministrativeAreaLevel2 = county;
      });
    }
    
    this.userForm.get('workLocation')?.setErrors(null);
    this.userForm.get('workLocation')?.updateValueAndValidity();
  }

  validateLocation = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
  
    // If selectedLocation exists and matches, it's valid
    if (this.selectedLocation === value) {
      return null;
    }
  
    // Otherwise, check if value is one of the options
    if (this.availableLocations.includes(value)) {
      return null;
    }
    
    return { invalidLocation: true };
  };

  validateWorkLocation = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
  
    // If selectedLocation exists and matches, it's valid
    if (this.selectedWorkLocation === value) {
      return null;
    }
  
    // Otherwise, check if value is one of the options
    if (this.availableWorkLocations.includes(value)) {
      return null;
    }
  
    return { invalidLocation: true };
  };

  minSelectedCheckboxes(min = 1) {
    return (formArray: AbstractControl): { [key: string]: any } | null => {
      let totalSelected = (formArray as FormArray).controls
                            .map(control => control.value)
                            .filter(checked => checked).length;

      const parent = formArray.parent;
      const selfDescribe = parent?.get('ethnicitySelfDescribe')?.value;
      const total = totalSelected + (selfDescribe ? 1 : 0);

      return total >= min ? null : { required: true };
    };
  }

  shouldDisableCheckboxEthnicity(index: number): boolean {
    const formArray = this.userForm.get('ethnicities') as FormArray;
    const selectedValues = formArray.value as boolean[];
    const selectedIndexes = selectedValues
                            .map((checked, i) => checked ? i : -1)
                            .filter(i => i !== -1);

    const selectedOptionValues = selectedIndexes.map(i => this.ethnicityOptions[i].value);
    const isSelfDescribeChecked = this.userForm.get('ethnicitySelfDescribe')?.value === true;
  
    const selectedDisableOptionValue = [
      ...selectedOptionValues, 
      isSelfDescribeChecked ? 'self-describe' : ''
    ].find(value => this.ethnicityDisableOptions.includes(value));

    return selectedDisableOptionValue
      ? this.ethnicityOptions[index].label !== selectedDisableOptionValue
      : false;
  }

  isSelfDescribeDisabledEthnicity(): boolean {
    const formArray = this.userForm.get('ethnicities') as FormArray;
    const selectedValues = formArray.value as boolean[];
    const selectedOptionValues = selectedValues
                            .map((checked, i) => checked ? this.ethnicityOptions[i].value : '')
                            .filter(Boolean);
  
    const isSelfDescribeChecked = this.userForm.get('ethnicitySelfDescribe')?.value === true;
  
    const selectedDisableOptionLabel = [
      ...selectedOptionValues, 
      isSelfDescribeChecked ? 'self-describe' : ''
    ].find(value => this.ethnicityDisableOptions.includes(value));
  
    return selectedDisableOptionLabel
      ? selectedDisableOptionLabel !== 'self-describe'
      : false;
  }

  evaluateSelections() {
    const formArray = this.userForm.get('ethnicities') as FormArray;
    const selectedValues = formArray.value as boolean[];
  
    const selectedIndexes = selectedValues
                            .map((checked, i) => checked ? i : -1)
                            .filter(i => i !== -1);
  
    const selectedOptionValues = selectedIndexes.map(i => this.ethnicityOptions[i].value);
    const isSelfDescribeChecked = this.userForm.get('ethnicitySelfDescribe')?.value === true;
  
    const selectedDisableOptionValue = [
      ...selectedOptionValues, 
      isSelfDescribeChecked ? 'self-describe' : ''
    ].find(value => this.ethnicityDisableOptions.includes(value));
    
    if (selectedDisableOptionValue) {
      selectedIndexes.forEach(i => {
        if (this.ethnicityOptions[i].value !== selectedDisableOptionValue) {
          formArray.at(i).setValue(false, { emitEvent: false });
        }
      });
  
      if (isSelfDescribeChecked && selectedDisableOptionValue !== 'self-describe') {
        this.userForm.get('ethnicitySelfDescribe')?.setValue(false, { emitEvent: false });
      }
  
      if (selectedDisableOptionValue !== 'self-describe') {
        this.userForm.get('ethnicitySelfDescribeText')?.setValue('', { emitEvent: false });
      }
    }

    if (isSelfDescribeChecked) {
      this.userForm.get('ethnicitySelfDescribeText')?.setValidators([selfDescibeTextValidator()]);
      this.userForm.get('ethnicitySelfDescribeText')?.enable({ emitEvent: false });
    } else {
      this.userForm.get('ethnicitySelfDescribeText')?.clearValidators();
      this.userForm.get('ethnicitySelfDescribeText')?.setValue('', { emitEvent: false });
      this.userForm.get('ethnicitySelfDescribeText')?.disable({ emitEvent: false });
    }

    this.userForm.get('ethnicities')?.updateValueAndValidity({ emitEvent: false });
    this.userForm.get('ethnicitySelfDescribeText')?.updateValueAndValidity({ emitEvent: false });
  
    this.cdr.markForCheck?.();
  }

  getSelectedOptions(): string[] {
    const arrayControl = this.userForm.get('ethnicities') as FormArray;
    const selected = this.ethnicityOptions
                      .filter((_, i) => arrayControl.at(i).value)
                      .map(option => option.value);
  
    if (
      this.userForm.get('ethnicitySelfDescribe')?.value && 
      this.userForm.get('ethnicitySelfDescribeText')?.value?.trim()
    ) {
      selected.push(this.userForm.get('ethnicitySelfDescribeText')?.value);
    }
  
    return selected;
  }

  onScroll() {
    const el = this.contentDiv.nativeElement;
    el.classList.add('show-scrollbar');

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      el.classList.remove('show-scrollbar');
    }, 1000);

    this.updateActiveSection();
  }

  scrollToSection(sectionId: string) {
    const section = this.contentDiv.nativeElement.querySelector(`#${sectionId}`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private updateActiveSection() {
    const container = this.contentDiv.nativeElement;
    const scrollTop = container.scrollTop;
    let current = this.sideNavSections[0].id;

    for (const section of this.sideNavSections) {
      const el = container.querySelector(`#${section.id}`);
      if (el) {
        const offset = (el as HTMLElement).offsetTop - container.offsetTop;
        if (scrollTop >= offset - 100) {
          current = section.id;
        }
      }
    }

    this.activeSection = current;
  }

  saveUserInfo() {
    this.userForm.markAllAsTouched();
    this.markAllOrgControlsTouched();
    if (this.userForm.invalid || this.hasInvalidOrgControls()) return;
    this.isSaving = true;

    forkJoin([
      this.userService.createEmailPreferences({
        emailPreferences: [
          {
            emailType: 'product',
            subscribed: this.emailPreferencesFormGroup.get('productEmails')?.value,
          },
          {
            emailType: 'marketing',
            subscribed: this.emailPreferencesFormGroup.get('marketingEmails')?.value,
          },
        ],
      }),
      this.biqApiService.saveUserInfo(
        this.getApiPayload()
      ),
      ...this.getWorkExperienceSaveObservables(),
    ]).subscribe({
      next: () => {
        // Invalidate the /me cache so the next fetch gets the updated profile
        this.saveBiqDataToStore();
        this.userService.invalidateMeCache();
        this.profileService.setUserProfileUpdated(true);
        this.showSuccessMessage();
        this.emailPreferencesFormGroup.markAsPristine();
        this.clearWorkExperiencePendingState();
        this.isSaveBtnEnabled = false;
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving email preferences:', error);
        this.showErrorMessage('Error while saving data. Please try again!');
        this.isSaving = false;
      }
    });
  }

  saveBiqDataToStore() {
    const formData = this.userForm.getRawValue();
    this.store.dispatch(updateUserBiq({ user: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      location: formData.location,
      birthYear: formData.birthYear ?? null,
      gender: formData.genderSelfDescribeText ? formData.genderSelfDescribeText : formData.gender,
      ethnicity: this.getSelectedOptions(),
      grade: formData.schoolYear,
      graduationYear: formData.graduationYear,
      schoolName: formData.schoolName,
      major: formData.graduationMajor?.value || null,
      isWorking: formData.isWorking,
      jobType: formData.jobType ?? null,
      futureLocation: formData.workLocation,
      futureLocationGooglePlaceId: this.selectedWorkLocationPlaceId,
      futureLocationAdministrativeAreaLevel2: this.selectedAdministrativeAreaLevel2,
    }}));
  }

  getApiPayload() {
    const formData = this.userForm.getRawValue();
    const userEthnicity = this.getSelectedOptions();
    return {
      first_name: formData.firstName,
      last_name: formData.lastName,
      location: formData.location,
      birth_year: formData.birthYear ?? null,
      gender: formData.genderSelfDescribeText ? formData.genderSelfDescribeText : formData.gender,
      ethnicity: userEthnicity,
      school_grade: formData.schoolYear,
      graduation_year: formData.graduationYear,
      school_name: formData.schoolName,
      graduation_major: formData.graduationMajor?.value || null,
      is_working: formData.isWorking,
      job_type: formData.jobType ?? null,
      future_location: formData.workLocation,
      desired_location_google_place_id: this.selectedWorkLocationPlaceId,
      desired_location_google_administrative_area_level_2: this.selectedAdministrativeAreaLevel2,
    }
  }

  onClickLink(key: string) {
    switch (key) {
      case 'resume':
        this.openResumeDialog()
        break;
      case 'survey':
        this.showRiasecScore();
        break;
      case 'edge-communication':
        this.openEdgeAssessment(key)
        break;
      case 'edge-behaviour':
        this.openEdgeAssessment(key)
        break;
      default:
        break;
    }
  }

  openResumeDialog() {
    this.dialog.open(AddResumeDialogComponent, {
      panelClass: 'add-resume-dialog',
      width: '95%',
      maxWidth: '1632px',
      maxHeight: '95vh',
      data: { mode: 'view', redirectTo: '/profile', section: 'profile' },
    });
  }

  getSurveyResults() {
    this.spinner.show();
    this.spinnerLoadingMessage = 'Loading survey results…';
    this.surveyService.getUserRiasecScore(environment.careerSurveyUuid).pipe(
      finalize(() => {
        this.spinner.hide();
        this.spinnerLoadingMessage = 'Survey results loaded.';
      })
    ).subscribe({
      next: (response) => {
        if (response?.riasec_score) {
          this.riasecScore = response;
          this.additionalLinks = this.additionalLinks.map(link => {
            if (
              link.key === 'survey' || 
              link.key === 'edge-communication' || 
              link.key === 'edge-behaviour'
            ) {
              return { ...link, display: true };
            }
            return link;
          });
        }
      },
      error: (error) => {
        console.log('Error while fetching riasec score: ', error);
        this.showErrorMessage('Error while fetching data!');
      }
    });
  }

  showRiasecScore() {
    if (this.riasecScore?.riasec_score) {
      this.dialog.open(ScoresComponent, {
        data: {
          riasec: this.riasecScore.riasec_score,
          source: 'myProfile'
        },
        disableClose: true,
        maxHeight: '90vh',
      });
    }
  }

  openEdgeAssessment(key: string) {
    if (key == 'edge-communication') {
      window.open(this.edgeCommunicationAssessmentUrl, '_blank');
    } else if (key == 'edge-behaviour') {
      window.open(this.edgeBehavioralAssessmentUrl, '_blank');
    }
  }

  showSuccessMessage() {
    this._snackBar.openFromComponent(SuccessSnackBarComponent, {
      ...SuccessDefaultOptions,
      data: { 
        message: 'Profile updated!',
      },
      duration: 3000,
    });
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: { 
        message: msg,
      },
      duration: 3000,
    });
  }

  loadReportedWorkExperiences() {
    this.reportedWorkExperienceService.getReportedWorkExperience().subscribe({
      next: (response) => {
        this.reportedWorkExperiences = response.reported_work_experiences;
        this.originalWorkExperiences.clear();
        for (const exp of this.reportedWorkExperiences) {
          this.originalWorkExperiences.set(exp.id, { ...exp });
        }
        this.pendingRemovals.clear();
        this.pendingUpdates.clear();
        this.initOrgControls();
      },
      error: (err) => console.error('Failed to load reported work experiences', err),
    });
  }

  initOrgControls() {
    this.orgControlMap.clear();
    this.orgFilteredMap.clear();
    for (const exp of this.reportedWorkExperiences) {
      const control = new FormControl(exp.employerName, {
        nonNullable: true,
        validators: [(c) => c.value?.trim() ? null : { required: true }],
      });
      this.orgControlMap.set(exp.id, control);
      this.orgFilteredMap.set(
        exp.id,
        control.valueChanges.pipe(
          startWith(''),
          map((value) => (value ? this.filterCompanies(value) : []))
        )
      );
    }
  }

  private filterCompanies(query: string): string[] {
    const lower = query.toLowerCase();
    const terms = lower.split(/\s+/).filter(Boolean);
    return FORTUNE_1000_COMPANIES.filter((company) => {
      const companyLower = company.toLowerCase();
      return terms.every((term) => companyLower.includes(term));
    }).slice(0, 20);
  }

  getOrgControl(id: number): FormControl<string> {
    return this.orgControlMap.get(id)!;
  }

  getFilteredOrgs(id: number): Observable<string[]> {
    return this.orgFilteredMap.get(id)!;
  }

  private hasInvalidOrgControls(): boolean {
    for (const control of this.orgControlMap.values()) {
      if (control.invalid) return true;
    }
    return false;
  }

  private markAllOrgControlsTouched(): void {
    for (const control of this.orgControlMap.values()) {
      control.markAsTouched();
    }
  }

  updateWorkType(exp: ReportedWorkExperience, newWorkType: string) {
    if (newWorkType === exp.workType) return;
    exp.workType = newWorkType;
    this.pendingUpdates.add(exp.id);
    this.checkIfFormChanged();
  }

  updateEmployerName(exp: ReportedWorkExperience, newName: string) {
    const trimmed = newName.trim();
    const control = this.getOrgControl(exp.id);
    control.markAsTouched();
    if (!trimmed || trimmed === exp.employerName) return;
    exp.employerName = trimmed;
    this.pendingUpdates.add(exp.id);
    this.checkIfFormChanged();
  }

  private getWorkExperienceSaveObservables(): Observable<any>[] {
    const observables: Observable<any>[] = [];

    for (const id of this.pendingRemovals) {
      observables.push(this.reportedWorkExperienceService.removeReportedWorkExperience(id));
    }

    for (const id of this.pendingUpdates) {
      if (this.pendingRemovals.has(id)) continue;
      const exp = this.reportedWorkExperiences.find(e => e.id === id);
      if (exp) {
        observables.push(this.reportedWorkExperienceService.updateReportedWorkExperience(exp.id, {
          work_type: exp.workType,
          employer_name: exp.employerName,
        }));
      }
    }

    return observables;
  }

  private clearWorkExperiencePendingState() {
    this.pendingRemovals.clear();
    this.pendingUpdates.clear();
    this.originalWorkExperiences.clear();
    for (const exp of this.reportedWorkExperiences) {
      this.originalWorkExperiences.set(exp.id, { ...exp });
    }
  }

  removeWorkExperience(id: number) {
    this.pendingRemovals.add(id);
    this.reportedWorkExperiences = this.reportedWorkExperiences.filter(e => e.id !== id);
    this.orgControlMap.delete(id);
    this.orgFilteredMap.delete(id);
    this.checkIfFormChanged();
  }

  openAddWorkExperience() {
    const dialogRef = this.dialog.open(JobLandingDialogComponent, {
      panelClass: 'job-landing-dialog',
      width: '95%',
      maxWidth: '800px',
      maxHeight: '95vh',
      data: { entrypoint: 'my_profile', section: 'profile' },
    });

    dialogRef.afterClosed().subscribe((result?: JobLandingDialogResult) => {
      if (result) {
        const sourceMap: Record<string, string> = {
          'Compass': 'compass',
          'Handshake': 'handshake',
          'LinkedIn': 'linked_in',
          'Networking or referral': 'networking_or_referral',
          'Other': 'other',
        };

        const payload: {
          work_type: string;
          employer_name: string;
          work_referrer?: string;
          work_referrer_other?: string;
        } = {
          work_type: result.roleType,
          employer_name: result.organization,
        };

        if (result.source) {
          const mappedSource = sourceMap[result.source];
          if (mappedSource && mappedSource !== 'other') {
            payload.work_referrer = mappedSource;
          } else {
            payload.work_referrer = 'other';
            payload.work_referrer_other = result.source;
          }
        }

        this.reportedWorkExperienceService.createReportedWorkExperience(payload).subscribe({
          next: () => this.loadReportedWorkExperiences(),
          error: (err) => {
            console.error('Failed to save reported work experience', err);
            this.showErrorMessage('Failed to add work experience.');
          },
        });
      }
    });
  }

  formatWorkType = formatWorkType;

  formatWorkReferrer(referrer: string | null, other: string | null): string {
    if (!referrer) return '';
    const map: Record<string, string> = {
      'compass': 'Compass',
      'handshake': 'Handshake',
      'linked_in': 'LinkedIn',
      'networking_or_referral': 'Networking or referral',
      'other': other || 'Other',
    };
    return map[referrer] || referrer;
  }
}
